import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';

interface BillingItem {
  readonly claimId: string;
  readonly claimNumber: string;
  readonly status: string;
  readonly totalClaimAmount: number;
  readonly billingPeriodFrom: string;
  readonly billingPeriodTo: string;
  readonly submissionDate: string | null;
}

/** L13.01 — Vendor Billing Inbox: claims in progress for this vendor. */
export function VendorBillingInboxPage(): ReactNode {
  const { token } = useAuth();
  const [items, setItems] = useState<readonly BillingItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    apiRequest<{ items: readonly BillingItem[] }>('/v1/vendor/billing/inbox', { token })
      .then(r => { if (alive) setItems(r.items); })
      .catch(e => { if (alive) setError((e as Error).message); });
    return () => { alive = false; };
  }, [token]);

  return (
    <div style={{ padding: 24 }}>
      <h1>Billing Inbox</h1>
      <Link to="/vendor/portal/billing/new" style={{ display: 'inline-block', padding: '8px 16px', background: '#0969da', color: '#fff', borderRadius: 6, textDecoration: 'none', marginBottom: 16 }}>+ New Claim</Link>
      {error && <p style={{ color: 'red' }}>{error}</p>}
      {!items && !error && <p>Loading...</p>}
      {items && items.length === 0 && <p>No billing claims found.</p>}
      {items && items.length > 0 && (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ background: '#f6f8fa', borderBottom: '1px solid #d0d7de' }}>
              <th style={{ padding: 8, textAlign: 'left' }}>Claim #</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Period</th>
              <th style={{ padding: 8, textAlign: 'right' }}>Amount (₹)</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Status</th>
              <th style={{ padding: 8, textAlign: 'left' }}>Submitted</th>
            </tr>
          </thead>
          <tbody>
            {items.map((c, i) => (
              <tr key={c.claimId ?? i} style={{ borderBottom: '1px solid #d0d7de' }}>
                <td style={{ padding: 8 }}><Link to={`/vendor/portal/billing/${c.claimId}`} style={{ color: '#0969da' }}>{c.claimNumber}</Link></td>
                <td style={{ padding: 8 }}>{c.billingPeriodFrom} – {c.billingPeriodTo}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{(c.totalClaimAmount ?? 0).toLocaleString('en-IN')}</td>
                <td style={{ padding: 8 }}><span style={{ padding: '2px 8px', borderRadius: 4, fontSize: 12, background: c.status === 'APPROVED_FOR_FINANCE' ? '#dafbe1' : c.status === 'REJECTED' ? '#ffebe9' : '#fff8c5' }}>{c.status}</span></td>
                <td style={{ padding: 8 }}>{c.submissionDate ? new Date(c.submissionDate).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
